import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import NewsHome from "@/pages/NewsHome";
import RedirectHandler from "@/components/RedirectHandler";
import CatchAllRedirect from "@/components/CatchAllRedirect";
import FaviconManifestInjector from "@/components/FaviconManifestInjector";
import { Toaster } from "@/components/ui/sonner";
import { getRole } from "@/lib/api";

// -----------------------------------------------------------------------------
// Code splitting — everything below is only downloaded when the user actually
// hits an admin, marketing or blog route. Keeps the initial JS bundle small
// for public visitors landing on `/`.
// -----------------------------------------------------------------------------
const AdminLogin        = lazy(() => import("@/pages/admin/AdminLogin"));
const ForgotPassword    = lazy(() => import("@/pages/admin/ForgotPassword"));
const ResetPassword     = lazy(() => import("@/pages/admin/ResetPassword"));
const ChangePassword    = lazy(() => import("@/pages/admin/ChangePassword"));
const AdminLayout       = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard         = lazy(() => import("@/pages/admin/Dashboard"));
const Sources           = lazy(() => import("@/pages/admin/Sources"));
const FeedApi           = lazy(() => import("@/pages/admin/FeedApi"));
const Telegram          = lazy(() => import("@/pages/admin/Telegram"));
const Ads               = lazy(() => import("@/pages/admin/Ads"));
const Settings          = lazy(() => import("@/pages/admin/Settings"));
const Credentials       = lazy(() => import("@/pages/admin/Credentials"));
const Users             = lazy(() => import("@/pages/admin/Users"));
const MarketingDashboard = lazy(() => import("@/pages/admin/marketing/MarketingDashboard"));
const SEOPages          = lazy(() => import("@/pages/admin/marketing/SEOPages"));
const Blog              = lazy(() => import("@/pages/admin/marketing/Blog"));
const PromoBannerAdmin  = lazy(() => import("@/pages/admin/marketing/PromoBanner"));
const Redirects         = lazy(() => import("@/pages/admin/marketing/Redirects"));
const Newsletter        = lazy(() => import("@/pages/admin/marketing/Newsletter"));
const RobotsTxt         = lazy(() => import("@/pages/admin/marketing/RobotsTxt"));
const Sitemap           = lazy(() => import("@/pages/admin/marketing/Sitemap"));
const HomePageSeo       = lazy(() => import("@/pages/admin/marketing/HomePageSeo"));
const NewsArchive       = lazy(() => import("@/pages/admin/marketing/NewsArchive"));
const SymbolSentiment   = lazy(() => import("@/pages/admin/marketing/SymbolSentiment"));
const SocialReport      = lazy(() => import("@/pages/admin/marketing/SocialReport"));
const AutoReports       = lazy(() => import("@/pages/admin/marketing/AutoReports"));
const GiftNiftyPageAdmin = lazy(() => import("@/pages/admin/marketing/GiftNiftyPageAdmin"));
const BlogList          = lazy(() => import("@/pages/BlogList"));
const BlogPost          = lazy(() => import("@/pages/BlogPost"));
const GiftNiftyLive     = lazy(() => import("@/pages/GiftNiftyLive"));

// Small suspense fallback — matches the paper background so the swap isn't jarring
const Fallback = () => (
    <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="font-sub text-[11px] uppercase tracking-[0.2em] text-black/40 animate-pulse">Loading&hellip;</div>
    </div>
);

// Guard: for pages that require super-admin, redirect marketing to /admin
function SuperOnly({ children }) {
    if (getRole() === "super") return children;
    return <Navigate to="/admin" replace />;
}

// Marketing-first Dashboard: if role is marketing, use the marketing dashboard;
// super-admins still get the super-admin dashboard.
function DashboardRouter() {
    if (getRole() === "marketing") return <MarketingDashboard />;
    return <Dashboard />;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <RedirectHandler />
                <FaviconManifestInjector />
                <Suspense fallback={<Fallback />}>
                    <Routes>
                        <Route path="/" element={<NewsHome />} />
                        <Route path="/gift-nifty-live" element={<GiftNiftyLive />} />
                        <Route path="/blog" element={<BlogList />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />

                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin/forgot-password" element={<ForgotPassword />} />
                        <Route path="/admin/reset-password" element={<ResetPassword />} />
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<DashboardRouter />} />

                            {/* Super-admin only sections */}
                            <Route path="sources" element={<SuperOnly><Sources /></SuperOnly>} />
                            <Route path="feed-api" element={<SuperOnly><FeedApi /></SuperOnly>} />
                            <Route path="telegram" element={<SuperOnly><Telegram /></SuperOnly>} />
                            <Route path="ads" element={<SuperOnly><Ads /></SuperOnly>} />
                            <Route path="settings" element={<SuperOnly><Settings /></SuperOnly>} />
                            <Route path="users" element={<SuperOnly><Users /></SuperOnly>} />

                            {/* Available to both super + marketing */}
                            <Route path="credentials" element={<Credentials />} />

                            {/* Marketing suite */}
                            <Route path="marketing" element={<MarketingDashboard />} />
                            <Route path="marketing/home-seo" element={<HomePageSeo />} />
                            <Route path="marketing/seo" element={<SEOPages />} />
                            <Route path="marketing/blog" element={<Blog />} />
                            <Route path="marketing/promo" element={<PromoBannerAdmin />} />
                            <Route path="marketing/redirects" element={<Redirects />} />
                            <Route path="marketing/newsletter" element={<Newsletter />} />
                            <Route path="marketing/robots" element={<RobotsTxt />} />
                            <Route path="marketing/sitemap" element={<Sitemap />} />
                            <Route path="marketing/news-archive" element={<NewsArchive />} />
                            <Route path="marketing/symbol-sentiment" element={<SymbolSentiment />} />
                            <Route path="marketing/social-report" element={<SocialReport />} />
                            <Route path="marketing/auto-reports" element={<AutoReports />} />
                            <Route path="marketing/gift-nifty-page" element={<GiftNiftyPageAdmin />} />
                            <Route path="change-password" element={<ChangePassword />} />
                        </Route>
                        <Route path="*" element={<CatchAllRedirect />} />
                    </Routes>
                </Suspense>
            </BrowserRouter>
            <Toaster position="bottom-right" richColors />
        </div>
    );
}

export default App;
